export const SEARCH_MIN_CHARS = 3;
export const SEARCH_DEBOUNCE_MS = 350;

const GROUP_KEYS = ["cities", "countries", "activities"];

function asList(value) {
  if (!Array.isArray(value)) return [];
  return value;
}

function mapCity(item = {}) {
  const cityName = String(item.cityName || item.label || "").trim();
  if (!cityName) return null;

  return {
    id: `city-${item.cityId ?? cityName}`,
    kind: "city",
    label: cityName,
    title: cityName,
    subtitle: "Activities",
    cityId: item.cityId ?? null,
    cityName,
    countryId: item.countryId ?? null,
    raw: item,
  };
}

function mapCountry(item = {}) {
  const countryId = item.countryId ?? item.id ?? null;
  const countryName = String(item.countryName || item.label || item.name || "").trim();
  if (!countryName) return null;

  return {
    id: `country-${countryId ?? countryName}`,
    kind: "country",
    label: countryName,
    title: countryName,
    subtitle: "Activities",
    countryId,
    countryName,
    raw: item,
  };
}

function mapActivity(item = {}) {
  const activityName = String(item.activityName || item.label || "").trim();
  if (!activityName) return null;

  return {
    id: `activity-${item.activityId ?? activityName}`,
    kind: "activity",
    label: activityName,
    title: activityName,
    subtitle: "Activity",
    activityId: item.activityId ?? null,
    activityName,
    cityId: item.cityId ?? null,
    countryId: item.countryId ?? null,
    slug: item.slug || "",
    raw: item,
  };
}

export function getSearchGroups(response = {}) {
  const data = response?.data || response || {};

  return {
    cities: asList(data.cityData || data.cities).map(mapCity).filter(Boolean),
    countries: asList(data.countryData || data.countries).map(mapCountry).filter(Boolean),
    activities: asList(data.activityData || data.activities).map(mapActivity).filter(Boolean),
  };
}

export function flattenSearchGroups(groups = {}) {
  return GROUP_KEYS.flatMap((key) => groups[key] || []);
}

export function getSearchPrefix(urlParams = {}, pageTitle = "", fallback = "") {
  return (
    urlParams.cityName ||
    urlParams.countryName ||
    urlParams.keywords ||
    pageTitle ||
    fallback ||
    ""
  );
}

export function findSearchMatch(response, { prefix = "", urlParams = {} } = {}) {
  const groups = getSearchGroups(response);

  if (urlParams.cityId) {
    const match = groups.cities.find((item) => Number(item.cityId) === Number(urlParams.cityId));
    if (match) return match;
  }

  if (urlParams.countryId) {
    const match = groups.countries.find(
      (item) => Number(item.countryId) === Number(urlParams.countryId)
    );
    if (match) return match;
  }

  if (urlParams.keywords) {
    const keyword = String(urlParams.keywords).toLowerCase();
    const match = flattenSearchGroups(groups).find(
      (item) => item.label.toLowerCase() === keyword
    );
    if (match) return match;
  }

  const normalized = String(prefix || "").trim().toLowerCase();
  if (!normalized) return flattenSearchGroups(groups)[0] || null;

  const items = flattenSearchGroups(groups);
  return (
    items.find((item) => item.label.toLowerCase() === normalized) ||
    items.find((item) => item.label.toLowerCase().includes(normalized)) ||
    null
  );
}

export function buildSearchListHref(selection) {
  const params = new URLSearchParams();

  if (selection?.kind === "city" && selection.cityId) {
    params.set("CityId", String(selection.cityId));
    params.set("CityName", selection.cityName || selection.label);
    if (selection.countryId) params.set("CountryId", String(selection.countryId));
  } else if (selection?.kind === "country" && selection.countryId) {
    params.set("CountryId", String(selection.countryId));
    params.set("CountryName", selection.countryName || selection.label);
  } else if (selection?.kind === "activity") {
    if (selection.cityId) params.set("CityId", String(selection.cityId));
    if (selection.countryId) params.set("CountryId", String(selection.countryId));
    if (selection.activityId) params.set("ActivityId", String(selection.activityId));
    params.set("Keywords", selection.activityName || selection.label);
  } else if (selection?.label) {
    params.set("Keywords", selection.label);
  }

  const query = params.toString();
  return query ? `/activity/list?${query}` : "/activity/list";
}

export function buildActivitySearchHref(selection) {
  if (selection?.kind === "city" || selection?.kind === "country") {
    return buildSearchListHref(selection);
  }

  if (selection?.kind === "activity") {
    const slug = String(selection.slug || selection.raw?.slug || "").trim();
    if (slug) return `/activity/${slug}`;
    return buildSearchListHref(selection);
  }

  return buildSearchListHref(selection);
}

export function createSearchDraft(selection) {
  return {
    label: selection?.label || "",
    selection: selection || null,
  };
}

export function getSearchDisplayLabel(selection, fallback = "") {
  if (!selection) return fallback;

  if (selection.kind === "city") {
    const city = selection.cityName || selection.label || "";
    const country = String(selection.raw?.countryName || selection.countryName || "").trim();
    return country ? `${city}, ${country}` : city || fallback;
  }

  return selection.label || fallback;
}

export function getListingDestinationLabel({
  selection = null,
  draftLabel = "",
  urlParams = {},
  pageTitle = "",
  fallback = "Search destination",
} = {}) {
  if (selection) {
    return getSearchDisplayLabel(selection, fallback);
  }

  const label = String(draftLabel || "").trim();
  if (label) return label;

  const city = String(urlParams.cityName || "").trim();
  const country = String(urlParams.countryName || "").trim();
  if (city && country) return `${city}, ${country}`;

  return (
    city ||
    country ||
    urlParams.keywords ||
    pageTitle ||
    fallback
  );
}

export function getHeaderSearchSummary(urlParams = {}, pageTitle = "", fallback = "") {
  return {
    location: getSearchPrefix(urlParams, pageTitle, fallback) || "Activities",
  };
}

export const SEARCH_GROUP_LABELS = {
  cities: "Cities",
  countries: "Countries",
  activities: "Activities",
};
